/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 * Based on https://github.com/mattdesl/shape2d-triangulate
 */

import {getBounds, indexOfPointInList, isClockwise, isCollinear, pointInPoly} from "App/utils/text/fonts/point_util";
import * as poly2tri from "App/lib/Poly2tri.js";

export function asPointSet(points): poly2tri.Point[] {
    let contour = [];

    for (let n = 0; n < points.length; n++) {
        let x = points[n][0];
        let y = points[n][1];

        let np = new poly2tri.Point(x, y);

        if (indexOfPointInList(np, contour) === -1) {
            if ((n === 0 || n === points.length - 1) || !isCollinear(points[n - 1], points[n], points[n + 1])) {
                contour.push(np);
            }
        }
    }
    return contour;
}

export function insideHole(poly: any, point: poly2tri.Point): boolean {
    for (let i = 0; i < poly.holes.length; i++) {
        let hole = poly.holes[i];
        if (pointInPoly(hole, point)) {
            return true;
        }
    }

    return false;
}

export function addSteinerPoints(poly: any, points: any, sweep: any): void {
    let bounds = getBounds(poly.contour);

    //ensure points are unique and not collinear
    points = asPointSet(points);

    for (let i = 0; i < points.length; i++) {
        let p = points[i];

        //fugly collinear fix ... gotta revisit this
        p.x += 0.5;
        p.y += 0.5;

        if (p.x <= bounds.minX || p.y <= bounds.minY || p.x >= bounds.maxX || p.y >= bounds.maxY)
            continue;

        if (pointInPoly(poly.contour, p) && !insideHole(poly, p)) {
            //We are in the polygon! Now make sure we're not in a hole..
            sweep.addPoint(new poly2tri.Point(p.x, p.y));
        }
    }
}

export function triangulate(shapes: any, steinerPoints: any): any[] {
    let windingClockwise = false;
    let sweep = null;

    let poly = {holes: [], contour: []};
    let allTris = [];

    shapes = Array.isArray(shapes) ? shapes : [shapes];

    steinerPoints = (steinerPoints && steinerPoints.length !== 0) ? steinerPoints : null;

    for (let j = 0; j < shapes.length; j++) {
        let points = shapes[j].points;

        let set = asPointSet(points);

        //OpenBaskerville-0.0.75 does some strange things
        //with the moveTo command, causing the decomposition
        //to give us an extra shape with only 1 point. This
        //simply skips a path if it can't make up a triangle..
        if (set.length < 3)
            continue;

        //check the winding order
        if (j == 0) {
            windingClockwise = isClockwise(set);
        }

        //if the sweep has already been created, maybe we're on a hole?
        if (sweep !== null) {
            let clock = isClockwise(set);

            //we have a hole...
            if (windingClockwise !== clock) {
                sweep.addHole(set);
                poly.holes.push(set);
            } else {
                //no hole, so it must be a new shape.
                //add our last shape
                if (steinerPoints !== null) {
                    addSteinerPoints(poly, steinerPoints, sweep);
                }

                sweep.triangulate();
                allTris = allTris.concat(sweep.getTriangles());

                //reset the sweep for next shape
                sweep = new poly2tri.SweepContext(set);
                poly = {holes: [], contour: points};
            }
        } else {
            sweep = new poly2tri.SweepContext(set);
            poly = {holes: [], contour: points};
        }
    }

    //if the sweep is still setup, then triangulate it
    if (sweep !== null) {
        if (steinerPoints !== null) {
            addSteinerPoints(poly, steinerPoints, sweep);
        }

        sweep.triangulate();
        allTris = allTris.concat(sweep.getTriangles());
    }
    return allTris;
}
